import { Link } from 'react-router-dom';
import {
  alcoholPerCalorieGainPct,
  calorieSavingsPct,
  formatPercent,
  priceSavingsPct,
  savingsWording,
} from '@/lib/metrics';
import {
  bestInCategory,
  filterByScope,
  groupCombinedView,
  nearbyAlternatives,
  type RankableDrink,
} from '@/lib/recommendations';
import { formatDrinkDisplayName } from '@/lib/displayName';
import { useCatalog } from '@/hooks/CatalogContext';
import type { CatalogRow } from '@/lib/catalog';

function toRankable(row: CatalogRow): RankableDrink {
  return {
    id: row.product.id,
    name: formatDrinkDisplayName(row.product.name),
    category: row.product.category,
    subtype: row.product.subtype,
    alcoholPer100Calories: row.alcoholPer100Calories,
    alcoholPerDollar: row.alcoholPerDollar,
    caloriesPerStandardDrink: row.caloriesPerStandardDrink,
    dollarsPerStandardDrink: row.dollarsPerStandardDrink,
  };
}

export function BaselinePanel() {
  const {
    baselineRow,
    setBaseline,
    rows,
    filteredSorted,
    prefs,
    setBaselineScope,
    filters,
  } = useCatalog();

  if (!baselineRow) return null;

  const baseline = toRankable(baselineRow);
  const inFilter = filteredSorted.some((r) => r.product.id === baseline.id);
  const pool = filterByScope(rows.map(toRankable), baseline, prefs.baselineScope);
  const activeRatio =
    prefs.sortKey === 'alcoholPerDollar'
      ? 'alcoholPerDollar'
      : 'alcoholPer100Calories';
  const { better, worse } = nearbyAlternatives(pool, baseline, activeRatio, 5);
  const best = bestInCategory(rows.map(toRankable), baseline, activeRatio, 5);
  const groups = groupCombinedView(pool, baseline);

  return (
    <section className="panel baseline-pin" aria-label="Baseline drink">
      <div>
        <p className="eyebrow">Baseline</p>
        <h2 style={{ margin: '0.15rem 0' }}>
          <Link to={`/drinks/${baselineRow.product.slug}/`}>
            {formatDrinkDisplayName(baselineRow.product.name)}
          </Link>
        </h2>
        <p className="muted" style={{ margin: 0 }}>
          {baselineRow.product.subtype} · optional comparison anchor
          {!inFilter && filters.category !== 'all'
            ? ' · currently outside active filters'
            : ''}
        </p>
      </div>
      <div className="row-actions">
        <label>
          <span className="sr-only">Alternative scope</span>
          <select
            className="select"
            value={prefs.baselineScope}
            onChange={(e) =>
              setBaselineScope(e.target.value as 'subtype' | 'category' | 'all')
            }
          >
            <option value="subtype">Same subtype</option>
            <option value="category">Same category</option>
            <option value="all">All drinks</option>
          </select>
        </label>
        <button type="button" className="btn" onClick={() => setBaseline(null)}>
          Clear baseline
        </button>
      </div>

      <div style={{ width: '100%' }}>
        <h3>Nearby on {activeRatio === 'alcoholPerDollar' ? 'alcohol / $' : 'alcohol / 100 cal'}</h3>
        <div className="compare-grid">
          <div>
            <strong>Better</strong>
            <ul>
              {better.length === 0 && <li className="muted">None in scope</li>}
              {better.map((d) => (
                <li key={d.id}>
                  {d.name}{' '}
                  <span className="muted">
                    (
                    {formatPercent(
                      alcoholPerCalorieGainPct(
                        d.alcoholPer100Calories ?? 0,
                        baseline.alcoholPer100Calories ?? 1,
                      ),
                    )}
                    )
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <strong>Worse</strong>
            <ul>
              {worse.length === 0 && <li className="muted">None in scope</li>}
              {worse.map((d) => (
                <li key={d.id}>{d.name}</li>
              ))}
            </ul>
          </div>
          <div>
            <strong>Best in category</strong>
            <ul>
              {best.map((d) => (
                <li key={d.id}>{d.name}</li>
              ))}
            </ul>
          </div>
        </div>

        <h3>Combined calories &amp; price (equivalent alcohol)</h3>
        <p className="muted">
          Grouped view — not a single multiplied score. “Better on both” means no
          worse on either metric and strictly better on at least one.
        </p>
        {(
          [
            ['lower-cal-lower-cost', 'Lower calories & lower cost'],
            ['lower-cal-higher-cost', 'Lower calories, higher cost'],
            ['higher-cal-lower-cost', 'Higher calories, lower cost'],
            ['higher-both', 'Higher on both'],
            ['ties', 'Ties'],
            ['insufficient', 'Insufficient data'],
          ] as const
        ).map(([key, label]) => (
          <details key={key} open={key === 'lower-cal-lower-cost'}>
            <summary>
              {label} ({groups[key].length})
            </summary>
            <ul>
              {groups[key].slice(0, 8).map((d) => (
                <li key={d.id}>
                  {d.name}
                  {d.caloriesPerStandardDrink != null &&
                    baseline.caloriesPerStandardDrink != null && (
                      <>
                        {' '}
                        —{' '}
                        {savingsWording(
                          calorieSavingsPct(
                            d.caloriesPerStandardDrink,
                            baseline.caloriesPerStandardDrink,
                          ),
                          'calories',
                        )}
                        {' · '}
                        {savingsWording(
                          priceSavingsPct(
                            d.dollarsPerStandardDrink ?? NaN,
                            baseline.dollarsPerStandardDrink ?? NaN,
                          ),
                          'price',
                        )}
                      </>
                    )}
                </li>
              ))}
            </ul>
          </details>
        ))}
      </div>
    </section>
  );
}
